import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { EnquiryProvider, captureAttribution } from "@/components/site/EnquiryContext";
import { Header, FloatingContact, ScrollManager } from "@/components/site/Chrome";
import { Footer } from "@/components/site/Footer";
import { EnquiryModal } from "@/components/site/EnquiryModal";
import Home from "@/pages/Home";
import Residential from "@/pages/Residential";
import Commercial from "@/pages/Commercial";

captureAttribution();

// Fixed background per page: [wide srcSet, tall srcSet, tall fallback]. Residential/commercial photos arrive pre-darkened.
const BACKGROUNDS = {
  "/": ["/img/hero-wide-1024.webp 1024w, /img/hero-wide-1672.webp 1672w", "/img/hero-640.webp 640w, /img/hero-941.webp 941w", "/img/hero-941.webp"],
  "/residential": ["/img/bg-res-wide-1024.webp 1024w, /img/bg-res-wide-1672.webp 1672w", "/img/bg-res-tall-640.webp 640w, /img/bg-res-tall-941.webp 941w", "/img/bg-res-tall-941.webp"],
  "/commercial": ["/img/bg-com-wide-1024.webp 1024w, /img/bg-com-wide-1672.webp 1672w", "/img/bg-com-tall-640.webp 640w, /img/bg-com-tall-941.webp 941w", "/img/bg-com-tall-941.webp"],
};

const WIDE_MQ = "(min-width: 768px) and (orientation: landscape)";
const useWide = () => {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE_MQ).matches);
  useEffect(() => {
    const mq = window.matchMedia(WIDE_MQ);
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return wide;
};

// Home: the owner's videos, looped as supplied (only compressed for the web) — one cut for portrait, one for landscape.
const HomeVideo = ({ wide }) => {
  const v = wide ? "home-bg-wide" : "home-bg-tall";
  return (
    <video key={v} className="site-bg-img" autoPlay muted loop playsInline preload="auto" poster={`/img/${v}-poster.webp`} data-testid="home-bg-video" data-variant={wide ? "wide" : "tall"}>
      <source src={`/img/${v}.mp4`} type="video/mp4" />
      <source src={`/img/${v}.webm`} type="video/webm" />
    </video>
  );
};

// Pinch-zoom guard: while zoomed in, flag <html> (CSS drops live blur) and pause the background video
// so the browser isn't re-rendering heavy effects on every zoom/pan frame.
const useZoomGuard = () => {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    let zoomed = false;
    const sync = () => {
      const z = vv.scale > 1.02;
      if (z === zoomed) return;
      zoomed = z;
      document.documentElement.classList.toggle("is-zoomed", z);
      const v = document.querySelector("[data-testid=home-bg-video]");
      if (v) { if (z) v.pause(); else v.play().catch(() => {}); }
    };
    vv.addEventListener("resize", sync);
    return () => { vv.removeEventListener("resize", sync); document.documentElement.classList.remove("is-zoomed"); };
  }, []);
};

const SiteBackground = () => {
  useZoomGuard();
  const { pathname } = useLocation();
  const wide = useWide();
  const key = BACKGROUNDS[pathname] ? pathname : "/";
  const [wideSet, tall, src] = BACKGROUNDS[key];
  return (
    <div className={`site-bg site-bg--page`} aria-hidden="true" data-testid="site-background" data-bg={key}>
      {key === "/" ? <HomeVideo wide={wide} /> : (
        <picture key={key}>
          <source media={WIDE_MQ} srcSet={wideSet} sizes="100vw" />
          <img src={src} srcSet={tall} sizes="100vw" alt="" width="941" height="1672" fetchPriority="high" decoding="async" className="site-bg-img" />
        </picture>
      )}
      <div className="site-bg-tint" />
    </div>
  );
};

const Layout = () => (
  <>
    <SiteBackground />
    <a href="#main" className="skip-link">Skip to content</a>
    <ScrollManager />
    <Header />
    <main id="main"><Outlet /></main>
    <Footer />
    <FloatingContact />
    <EnquiryModal />
  </>
);

function App() {
  return (
    <BrowserRouter>
      <EnquiryProvider>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="residential" element={<Residential />} />
            <Route path="commercial" element={<Commercial />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </EnquiryProvider>
    </BrowserRouter>
  );
}

export default App;
